/**
 * SavedIndicator: dot plus the word. Always words, never the dot alone. A polite live region, unless `live` is false
 * (the page says the state itself). `short` shows shorter words (phones); the full words stay what is read and the tooltip.
 * (cockatrice-ui `src/ui/SavedIndicator.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/saved-indicator.css';
import { mergeProps, splitProps } from 'solid-js';
import { el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface SavedIndicatorProps extends Attributes {
  /** Save state. Default "saved". */
  readonly state?: 'saved' | 'saving' | 'offline' | 'error' | undefined;
  /** Override the words. */
  readonly label?: string | undefined;
  /** Shorter words shown in their place (a phone's top bar); the full words are still read, and are the tooltip. */
  readonly short?: string | undefined;
  /** A polite live region (role="status"). Default true; false where the page announces the state once itself. */
  readonly live?: boolean | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

const WORDS = {
  saved: 'ui.saved-indicator.saved',
  saving: 'ui.saved-indicator.saving',
  offline: 'ui.saved-indicator.offline',
  error: 'ui.saved-indicator.error',
} as const;

export default function SavedIndicator(props: SavedIndicatorProps): Child {
  const [local, rest] = splitProps(props, ['state', 'label', 'short', 'live', 'class']);
  const state = (): keyof typeof WORDS => local.state ?? 'saved';
  const words = (): string => (local.label !== undefined && local.label !== '' ? local.label : uiText()(WORDS[state()]));
  const short = (): boolean => local.short !== undefined && local.short !== '';
  const live = (): boolean => local.live !== false;
  return el(
    'span',
    mergeProps({
      class: () => `ui-saved-indicator ui-saved-indicator--${state()}${local.class !== undefined && local.class !== '' ? ` ${local.class}` : ''}`,
      role: () => (live() ? 'status' : undefined),
      'aria-live': () => (live() ? 'polite' : undefined),
      title: () => (short() ? words() : undefined),
    }, rest),
    el('span', { class: 'ui-saved-indicator__dot', 'aria-hidden': 'true' }),
    when(
      short,
      () => [el('span', { 'aria-hidden': 'true' }, () => local.short), el('span', { class: 'sr-only' }, words)],
      () => words,
    ),
  );
}
